// 개인정보 수집·이용 동의 게이트.
//
// Stands between the access password and the questionnaire: the site reads the notice,
// ticks the box, and only then does the server hand over any questions at all. The gate
// is enforced in the API (services/Feasibility/sitePublic.js) — this is the reading of it.
//
// The language comes from the page's own toggle, and what is on screen when the button is
// pressed is what gets snapshotted — so the toggle is not cosmetic here even though it now
// lives in the header, where it moves the whole screen rather than this notice alone.
import React, { useState } from "react";
import styled from "styled-components";
import { narrativeViewerStyles } from "../Feasibility/FeasibilityEditor/narrativeStyles";
import { withTableWrappers } from "../Feasibility/FeasibilityEditor/narrativeHtml";
import { siteText } from "./siteText";

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const Heading = styled.h2`
  font-size: 17px;
  color: #212529;
  margin: 0;
`;

const Notice = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fafafa;
  max-height: 46vh;
  overflow-y: auto;

  ${narrativeViewerStyles}
`;

const AgreeRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 18px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.$checked ? "#0d6efd" : "#dee2e6")};
  border-radius: 6px;
  background: ${(p) => (p.$checked ? "#f0f7ff" : "#fff")};
  font-size: 14px;
  color: #212529;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 17px;
  height: 17px;
  margin-top: 1px;
  flex-shrink: 0;
  cursor: pointer;
`;

const Required = styled.span`
  color: #dc3545;
  font-weight: 600;
  margin-right: 4px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: 9px 22px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: #0d6efd;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.9); }
`;

const ErrorMsg = styled.div`
  font-size: 13px;
  color: #dc3545;
`;

const Hint = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #868e96;
`;

export default function SiteConsentGate({ consent, lang, onAgree }) {
  // The tick survives a language switch: the two templates are the same notice in two
  // languages, so switching only restates what was already read. What gets recorded is
  // still the language on screen when the button is pressed.
  const [checked, setChecked] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");

  const tpl = consent?.templates?.[lang] || {};
  const t = siteText(lang);

  async function handleSubmit() {
    setBusy(true);
    setError("");
    try {
      await onAgree(lang);
      // Success swaps this gate out for the questionnaire, so there is no busy flag left
      // to clear — resetting it here would be a state update on an unmounted component.
    } catch {
      setError(t.consentFail);
      setBusy(false);
    }
  }

  return (
    <>
      <Head>
        <Heading>{tpl.title || t.consentAgree}</Heading>
      </Head>

      <Notice dangerouslySetInnerHTML={{ __html: withTableWrappers(tpl.bodyHtml) || "" }} />

      <AgreeRow $checked={checked}>
        <Checkbox
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <span><Required>{t.consentRequired}</Required>{t.consentAgree}</span>
      </AgreeRow>

      <ActionRow>
        <Btn type="button" onClick={handleSubmit} disabled={!checked || busy}>
          {busy ? t.consentBusy : t.consentSubmit}
        </Btn>
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </ActionRow>

      <Hint>{t.consentHint}</Hint>
    </>
  );
}
